import React, { useState } from 'react';
import { X, Plus, Trash2, FileText, Save } from 'lucide-react';
import { jobsAPI } from "../../services/api";
import { toast } from "react-toastify";

const EditJobModal = ({ job, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    jobCode: job.jobCode || '',
    client: job.client || '',
    jobTitle: job.jobTitle || '',
    department: job.department || '',
    jobStatus: job.jobStatus || 'OPEN',
    jobPriority: job.jobPriority || 'MEDIUM',
    jobLocation: Array.isArray(job.jobLocation) ? job.jobLocation : (job.jobLocation ? [job.jobLocation] : []),
    workLocation: job.workLocation || '',
    workModel: job.workModel || 'HYBRID',
    workingHours: job.workingHours || '',
    jobType: job.jobType || 'FULL_TIME',
    numberOfOpenings: job.numberOfOpenings || '1',
    primarySkills: Array.isArray(job.primarySkills) ? job.primarySkills : (job.primarySkills ? [job.primarySkills] : []),
    secondarySkills: Array.isArray(job.secondarySkills) ? job.secondarySkills : (job.secondarySkills ? [job.secondarySkills] : []),
    experienceMin: job.experienceMin || '',
    experienceMax: job.experienceMax || '',
    jobDescription: job.jobDescription || '',
    hiringManagerName: job.hiringManagerName || '',
    hiringManagerEmail: job.hiringManagerEmail || '',
    assignedTo: job.assignedTo || '',
    compensationCurrency: job.compensationCurrency || 'USD',
    compensationPaytype: job.compensationPaytype || '',
    compensationMin: job.compensationMin || '',
    compensationMax: job.compensationMax || '',
    vendorCommercials: job.vendorCommercials || '',
    education: job.education || '',
    preferredInstitution: job.preferredInstitution || '',
    noticePeriod: job.noticePeriod || '',
    workAuthorization: job.workAuthorization || '',
    visaSponsorship: job.visaSponsorship || '',
    backgroundCheck: job.backgroundCheck || '',
    securityClearance: job.securityClearance || '',
    licenseCertifications: job.licenseCertifications || '',
    idealCandidateProfile: job.idealCandidateProfile || '',
    interviewMode: job.interviewMode || '',
    interviewRounds: Array.isArray(job.interviewRounds) ? job.interviewRounds : (job.interviewRounds ? [job.interviewRounds] : []),
    screeningQuestions: Array.isArray(job.screeningQuestions) ? job.screeningQuestions : []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newJobLocation, setNewJobLocation] = useState('');
  const [newPrimarySkill, setNewPrimarySkill] = useState('');
  const [newSecondarySkill, setNewSecondarySkill] = useState('');
  const [newInterviewRound, setNewInterviewRound] = useState('');

  // Style constants
  const focusRingStyle = { boxShadow: "0 0 0 3px rgba(28, 78, 109, 0.1)" };
  const primaryBg = { backgroundColor: "#1C4E6D" };
  const primaryColor = { color: "#1C4E6D" };
  const primaryLightBg = { backgroundColor: "#e8f4f9" };

  const inputClass = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2";
  const inputErrorClass = (field) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${errors[field] ? 'border-red-500' : 'border-gray-300'}`;

  const jobStatusOptions = ['OPEN', 'CLOSED', 'ON_HOLD', 'DRAFT'];
  const priorityOptions = ['HIGH', 'MEDIUM', 'LOW'];
  const workModelOptions = ['REMOTE', 'ONSITE', 'HYBRID'];
  const jobTypeOptions = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY', 'INTERNSHIP'];
  const currencyOptions = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD'];
  const compensationPaytypeOptions = ['ANNUAL', 'HOURLY', 'MONTHLY', 'PROJECT_BASED'];
  const interviewModeOptions = ['IN_PERSON', 'VIRTUAL', 'PHONE', 'ASSESSMENT'];
  const workAuthorizationOptions = ['CITIZEN', 'GREEN_CARD', 'WORK_VISA', 'TN_VISA', 'OPT_CPT', 'NO_AUTHORIZATION'];
  const visaSponsorshipOptions = ['YES', 'NO', 'DEPENDS'];
  const backgroundCheckOptions = ['YES', 'NO', 'DEPENDS'];
  const securityClearanceOptions = ['NONE', 'BASIC', 'SECRET', 'TOP_SECRET'];
  const educationOptions = ['HIGH_SCHOOL', 'ASSOCIATE', 'BACHELORS', 'MASTERS', 'PHD', 'DIPLOMA', 'CERTIFICATION'];
  const noticePeriodOptions = ['IMMEDIATE', '15_DAYS', '30_DAYS', '45_DAYS', '60_DAYS', '90_DAYS', 'MORE_THAN_90_DAYS'];

  const addJobLocation = () => {
    if (newJobLocation.trim() && !formData.jobLocation.includes(newJobLocation.trim())) {
      setFormData(prev => ({ ...prev, jobLocation: [...prev.jobLocation, newJobLocation.trim()] }));
      setNewJobLocation('');
    }
  };
  const removeJobLocation = (index) => setFormData(prev => ({ ...prev, jobLocation: prev.jobLocation.filter((_, i) => i !== index) }));

  const addPrimarySkill = () => {
    if (newPrimarySkill.trim() && !formData.primarySkills.includes(newPrimarySkill.trim())) {
      setFormData(prev => ({ ...prev, primarySkills: [...prev.primarySkills, newPrimarySkill.trim()] }));
      setNewPrimarySkill('');
    }
  };
  const removePrimarySkill = (index) => setFormData(prev => ({ ...prev, primarySkills: prev.primarySkills.filter((_, i) => i !== index) }));

  const addSecondarySkill = () => {
    if (newSecondarySkill.trim() && !formData.secondarySkills.includes(newSecondarySkill.trim())) {
      setFormData(prev => ({ ...prev, secondarySkills: [...prev.secondarySkills, newSecondarySkill.trim()] }));
      setNewSecondarySkill('');
    }
  };
  const removeSecondarySkill = (index) => setFormData(prev => ({ ...prev, secondarySkills: prev.secondarySkills.filter((_, i) => i !== index) }));

  const addInterviewRound = () => {
    if (newInterviewRound.trim() && !formData.interviewRounds.includes(newInterviewRound.trim())) {
      setFormData(prev => ({ ...prev, interviewRounds: [...prev.interviewRounds, newInterviewRound.trim()] }));
      setNewInterviewRound('');
    }
  };
  const removeInterviewRound = (index) => setFormData(prev => ({ ...prev, interviewRounds: prev.interviewRounds.filter((_, i) => i !== index) }));

  const addScreeningQuestion = () => {
    setFormData(prev => ({
      ...prev,
      screeningQuestions: [...prev.screeningQuestions, { questionId: Date.now().toString(), questionText: '', questionType: 'SHORT_ANSWER', options: [], required: false }]
    }));
  };

  const updateScreeningQuestion = (index, field, value) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const addQuestionOption = (questionIndex) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: [...updatedQuestions[questionIndex].options, ''] };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      const updatedOptions = [...updatedQuestions[questionIndex].options];
      updatedOptions[optionIndex] = value;
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      const updatedOptions = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const removeScreeningQuestion = (index) => setFormData(prev => ({ ...prev, screeningQuestions: prev.screeningQuestions.filter((_, i) => i !== index) }));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.client.trim()) newErrors.client = 'Client is required';
    if (!formData.numberOfOpenings || parseInt(formData.numberOfOpenings) < 1) newErrors.numberOfOpenings = 'Number of openings must be at least 1';
    if (formData.experienceMax && formData.experienceMin && parseInt(formData.experienceMax) < parseInt(formData.experienceMin))
      newErrors.experienceMax = 'Max experience must be greater than or equal to min experience';
    if (formData.compensationMax && formData.compensationMin && parseFloat(formData.compensationMax) < parseFloat(formData.compensationMin))
      newErrors.compensationMax = 'Max compensation must be greater than or equal to min compensation';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error('Please fix the errors in the form'); return; }
    setLoading(true);
    try {
      const jobData = {
        ...formData,
        numberOfOpenings: parseInt(formData.numberOfOpenings) || 1,
        experienceMin: formData.experienceMin ? parseInt(formData.experienceMin) : null,
        experienceMax: formData.experienceMax ? parseInt(formData.experienceMax) : null,
        compensationMin: formData.compensationMin ? parseFloat(formData.compensationMin) : null,
        compensationMax: formData.compensationMax ? parseFloat(formData.compensationMax) : null,
        updatedAt: new Date().toISOString(),
        updatedBy: JSON.parse(localStorage.getItem("userProfile"))?.email || 'system'
      };
      const response = await jobsAPI.updateJob(job.id, jobData);
      toast.success('🎉 Job updated successfully!');
      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to update job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? e.target.checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-4xl h-full flex flex-col animate-slideInRight overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Job Posting</h2>
            <p className="text-sm text-gray-600">Job Code: {formData.jobCode}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* Section 1: Basic Information */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Code</label>
                <input type="text" name="jobCode" value={formData.jobCode} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="Auto-generated if empty" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <input type="text" name="client" value={formData.client} onChange={handleChange}
                  className={inputErrorClass('client')} style={focusRingStyle} placeholder="Client Name" />
                {errors.client && <p className="text-sm text-red-600 mt-1">{errors.client}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange}
                  className={inputErrorClass('jobTitle')} style={focusRingStyle} placeholder="Senior Developer" />
                {errors.jobTitle && <p className="text-sm text-red-600 mt-1">{errors.jobTitle}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input type="text" name="department" value={formData.department} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="Engineering" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Status</label>
                <select name="jobStatus" value={formData.jobStatus} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  {jobStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Priority</label>
                <select name="jobPriority" value={formData.jobPriority} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  {priorityOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Section 2: Job Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Job Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Model</label>
                <select name="workModel" value={formData.workModel} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  {workModelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                <select name="jobType" value={formData.jobType} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  {jobTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Openings *</label>
                <input type="number" name="numberOfOpenings" value={formData.numberOfOpenings}
                  onChange={handleChange} min="1" className={inputErrorClass('numberOfOpenings')} style={focusRingStyle} />
                {errors.numberOfOpenings && <p className="text-sm text-red-600 mt-1">{errors.numberOfOpenings}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Location</label>
                <input type="text" name="workLocation" value={formData.workLocation} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="e.g., New York, NY" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours</label>
                <input type="text" name="workingHours" value={formData.workingHours} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="e.g., 9 AM - 6 PM" />
              </div>
            </div>

            {/* Job Locations */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Job Locations</label>
              <div className="flex gap-2">
                <input type="text" value={newJobLocation} onChange={(e) => setNewJobLocation(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={focusRingStyle} placeholder="Add job location" />
                <button type="button" onClick={addJobLocation}
                  className="px-4 py-3 text-white rounded-lg" style={primaryBg}>
                  <Plus size={20} />
                </button>
              </div>
              {formData.jobLocation.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.jobLocation.map((location, index) => (
                    <div key={index} className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg">
                      <span className="text-sm">{location}</span>
                      <button type="button" onClick={() => removeJobLocation(index)} className="text-gray-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Section 3: Skills & Experience */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Skills & Experience</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Experience (years)</label>
                <input type="number" name="experienceMin" value={formData.experienceMin}
                  onChange={handleChange} min="0" className={inputClass} style={focusRingStyle} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Experience (years)</label>
                <input type="number" name="experienceMax" value={formData.experienceMax}
                  onChange={handleChange} min="0" className={inputErrorClass('experienceMax')} style={focusRingStyle} />
                {errors.experienceMax && <p className="text-sm text-red-600 mt-1">{errors.experienceMax}</p>}
              </div>
            </div>

            {/* Primary Skills */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Primary Skills</label>
              <div className="flex gap-2">
                <input type="text" value={newPrimarySkill} onChange={(e) => setNewPrimarySkill(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={focusRingStyle} placeholder="Add primary skill" />
                <button type="button" onClick={addPrimarySkill}
                  className="px-4 py-3 text-white rounded-lg" style={primaryBg}>
                  <Plus size={20} />
                </button>
              </div>
              {formData.primarySkills.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.primarySkills.map((skill, index) => (
                    <div key={index} className="flex items-center gap-1 px-3 py-2 rounded-lg" style={primaryLightBg}>
                      <span className="text-sm" style={primaryColor}>{skill}</span>
                      <button type="button" onClick={() => removePrimarySkill(index)} className="hover:text-red-500" style={primaryColor}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Secondary Skills */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Secondary Skills</label>
              <div className="flex gap-2">
                <input type="text" value={newSecondarySkill} onChange={(e) => setNewSecondarySkill(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={focusRingStyle} placeholder="Add secondary skill" />
                <button type="button" onClick={addSecondarySkill}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  <Plus size={20} />
                </button>
              </div>
              {formData.secondarySkills.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.secondarySkills.map((skill, index) => (
                    <div key={index} className="flex items-center gap-1 px-3 py-2 bg-green-50 rounded-lg">
                      <span className="text-sm text-green-700">{skill}</span>
                      <button type="button" onClick={() => removeSecondarySkill(index)} className="text-green-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Section 4: Job Description & Management */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Job Description & Management</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Job Description</label>
              <textarea name="jobDescription" value={formData.jobDescription} onChange={handleChange} rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                style={focusRingStyle} placeholder="Enter detailed job description..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager Name</label>
                <input type="text" name="hiringManagerName" value={formData.hiringManagerName} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="Manager Name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager Email</label>
                <input type="email" name="hiringManagerEmail" value={formData.hiringManagerEmail} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="manager@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <input type="text" name="assignedTo" value={formData.assignedTo} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="Recruiter name" />
              </div>
            </div>
          </section>

          {/* Section 5: Compensation */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Compensation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Compensation</label>
                <input type="number" name="compensationMin" value={formData.compensationMin} onChange={handleChange}
                  min="0" step="0.01" className={inputClass} style={focusRingStyle} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Compensation</label>
                <input type="number" name="compensationMax" value={formData.compensationMax} onChange={handleChange}
                  min="0" step="0.01" className={inputErrorClass('compensationMax')} style={focusRingStyle} />
                {errors.compensationMax && <p className="text-sm text-red-600 mt-1">{errors.compensationMax}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select name="compensationCurrency" value={formData.compensationCurrency} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
                <select name="compensationPaytype" value={formData.compensationPaytype} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select pay type</option>
                  {compensationPaytypeOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Commercials</label>
              <input type="text" name="vendorCommercials" value={formData.vendorCommercials} onChange={handleChange}
                className={inputClass} style={focusRingStyle} placeholder="Vendor commercial terms" />
            </div>
          </section>

          {/* Section 6: Education & Requirements */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Education & Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <select name="education" value={formData.education} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select education level</option>
                  {educationOptions.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Institution</label>
                <input type="text" name="preferredInstitution" value={formData.preferredInstitution} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="e.g., MIT, Stanford" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period</label>
                <select name="noticePeriod" value={formData.noticePeriod} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select notice period</option>
                  {noticePeriodOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Authorization</label>
                <select name="workAuthorization" value={formData.workAuthorization} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select work authorization</option>
                  {workAuthorizationOptions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visa Sponsorship</label>
                <select name="visaSponsorship" value={formData.visaSponsorship} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select visa sponsorship</option>
                  {visaSponsorshipOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Check</label>
                <select name="backgroundCheck" value={formData.backgroundCheck} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select background check</option>
                  {backgroundCheckOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Security Clearance</label>
                <select name="securityClearance" value={formData.securityClearance} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select security clearance</option>
                  {securityClearanceOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License & Certifications</label>
                <input type="text" name="licenseCertifications" value={formData.licenseCertifications} onChange={handleChange}
                  className={inputClass} style={focusRingStyle} placeholder="Required licenses/certifications" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ideal Candidate Profile</label>
              <textarea name="idealCandidateProfile" value={formData.idealCandidateProfile} onChange={handleChange}
                rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                style={focusRingStyle} placeholder="Describe the ideal candidate..." />
            </div>
          </section>

          {/* Section 7: Interview Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Interview Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interview Mode</label>
                <select name="interviewMode" value={formData.interviewMode} onChange={handleChange}
                  className={inputClass} style={focusRingStyle}>
                  <option value="">Select interview mode</option>
                  {interviewModeOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Interview Rounds */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Interview Rounds</label>
              <div className="flex gap-2">
                <input type="text" value={newInterviewRound} onChange={(e) => setNewInterviewRound(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={focusRingStyle} placeholder="Add interview round" />
                <button type="button" onClick={addInterviewRound}
                  className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  <Plus size={20} />
                </button>
              </div>
              {formData.interviewRounds.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.interviewRounds.map((round, index) => (
                    <div key={index} className="flex items-center gap-1 px-3 py-2 bg-purple-50 rounded-lg">
                      <span className="text-sm text-purple-700">{round}</span>
                      <button type="button" onClick={() => removeInterviewRound(index)} className="text-purple-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Section 8: Screening Questions */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Screening Questions</h3>
              <button type="button" onClick={addScreeningQuestion}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                <Plus size={16} />
                Add Question
              </button>
            </div>

            {formData.screeningQuestions.map((question, qIndex) => (
              <div key={question.questionId} className="p-4 border border-gray-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Question {qIndex + 1}</h4>
                  <button type="button" onClick={() => removeScreeningQuestion(qIndex)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                    <input type="text" value={question.questionText}
                      onChange={(e) => updateScreeningQuestion(qIndex, 'questionText', e.target.value)}
                      className={inputClass} style={focusRingStyle} placeholder="Enter your question..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                      <select value={question.questionType}
                        onChange={(e) => updateScreeningQuestion(qIndex, 'questionType', e.target.value)}
                        className={inputClass} style={focusRingStyle}>
                        <option value="SHORT_ANSWER">Short Answer</option>
                        <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                      </select>
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center">
                        <input type="checkbox" checked={question.required}
                          onChange={(e) => updateScreeningQuestion(qIndex, 'required', e.target.checked)}
                          className="rounded border-gray-300 mr-2" />
                        <span className="text-sm text-gray-700">Required</span>
                      </label>
                    </div>
                  </div>
                  {question.questionType === 'MULTIPLE_CHOICE' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Options</label>
                        <button type="button" onClick={() => addQuestionOption(qIndex)}
                          className="flex items-center gap-1 text-sm hover:opacity-80" style={primaryColor}>
                          <Plus size={14} />
                          Add Option
                        </button>
                      </div>
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center gap-2">
                          <input type="text" value={option}
                            onChange={(e) => updateQuestionOption(qIndex, oIndex, e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                            style={focusRingStyle} placeholder={`Option ${oIndex + 1}`} />
                          <button type="button" onClick={() => removeQuestionOption(qIndex, oIndex)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {formData.screeningQuestions.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="mx-auto text-gray-400 mb-2" size={24} />
                <p className="text-gray-500">No screening questions added</p>
                <p className="text-sm text-gray-400">Click "Add Question" to create screening questions</p>
              </div>
            )}
          </section>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSubmit} disabled={loading}
            className="flex-1 px-6 py-3 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            style={primaryBg}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = "#153a4f"}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = "#1C4E6D"}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditJobModal;